// POS ödəniş gateway-i — cloud-un masa ödəniş/ön-ödəniş axınını pos_local
// /v1/orders/:id/payments + /close əməliyyatlarına bağlayır.
//
// Cloud ↔ pos_local fərqləri (vacib):
//   • pos_local-da ENDİRİM anlayışı yoxdur — order total = item cəmidir. Front
//     `discount` (%) və `prepaid_amount` göndərir; aradakı fərqi `other` method-lu
//     "balans bağlama" ödənişi kimi qeyd edib order-i fully-paid vəziyyətə gətiririk
//     (əks halda /close → ORDER_NOT_FULLY_PAID).
//   • cloud method-ları (cash/bank/customer_balance) → pos_local (cash/card/wallet).
//   • cloud `prepayments` = order hələ açıqkən qismən ödəniş → pos_local payment
//     (close ETMƏDƏN). Silmə → payment void.

import axios from 'axios';
import { base_url } from '../index';
import { posRequest } from './posClient';
import { ensureOpenShift } from './shiftGateway';

// ── tiplər ───────────────────────────────────────────────────────────────────
type PosMethod = 'cash' | 'card' | 'wallet' | 'voucher' | 'other';

interface PosPaymentRow {
  id: string;
  method: PosMethod;
  amountCents: number;
  status: 'captured' | 'voided' | 'refund';
  paidAt: number; // epoch ms
}

interface PosPaymentsList {
  payments: PosPaymentRow[];
  balance: { orderId: string; netPaidCents: number; tipsCents: number; rowCount: number };
}

interface PosOrderTotals {
  totals: { totalCents: number };
}

interface CloseBillShare {
  type?: string; // cash | bank | customer_balance
  amount?: number;
  customer_id?: string | number | null;
}

export interface CloseBillBody {
  discount?: number; // faiz
  total_amount?: number; // endirimdən əvvəl
  prepaid_amount?: number;
  discounted_total?: number; // indi ödəniləcək (endirim + ön ödəniş düşülmüş)
  shares?: CloseBillShare[];
}

export interface AddPrepaymentBody {
  amount?: number | string;
  type?: string; // cash | bank | transfer
  date?: string;
}

interface GatewayResult {
  status: number;
  data: unknown;
}

// ── köməkçilər ────────────────────────────────────────────────────────────────

/** Adapter-uyğun xəta (adapter.ts err.posStatus/posCode oxuyur → axios response). */
function posError(status: number, message: string, code: string): Error {
  const e = new Error(message) as Error & { posStatus?: number; posCode?: string };
  e.posStatus = status;
  e.posCode = code;
  return e;
}

function throwFromRes(res: { error?: { code: string; message: string }; _status?: number }): never {
  const status = (res as { _status?: number })._status ?? 400;
  throw posError(status, res.error?.message || 'POS ödəniş xətası', res.error?.code || 'POS_ERROR');
}

/** cloud ödəniş tipi → pos_local method. */
function mapMethod(type: string | undefined): PosMethod {
  switch ((type ?? '').toLowerCase()) {
    case 'cash':
      return 'cash';
    case 'bank':
    case 'transfer':
    case 'card':
      return 'card';
    case 'customer_balance':
    case 'wallet':
      return 'wallet';
    default:
      return 'other';
  }
}

/** pos_local method → cloud `prepayments` siyahısının `type`-i (yalnız cash/bank). */
function unmapMethod(method: PosMethod): 'cash' | 'bank' {
  return method === 'cash' ? 'cash' : 'bank';
}

function toCents(amount: number | string | undefined): number {
  return Math.round(Number(amount ?? 0) * 100);
}

async function recordPayment(orderId: string, method: PosMethod, amountCents: number): Promise<void> {
  if (amountCents <= 0) return;
  const res = await posRequest({
    method: 'POST',
    path: `/v1/orders/${orderId}/payments`,
    body: { method, amountCents },
  });
  if (!res.ok) throwFromRes(res);
}

async function fetchTotalCents(orderId: string): Promise<number> {
  const o = await posRequest<PosOrderTotals>({ method: 'GET', path: `/v1/orders/${orderId}` });
  if (!o.ok || !o.data) throwFromRes(o);
  return o.data.totals?.totalCents ?? 0;
}

async function fetchNetPaidCents(orderId: string): Promise<number> {
  const p = await posRequest<PosPaymentsList>({
    method: 'GET',
    path: `/v1/orders/${orderId}/payments`,
  });
  if (!p.ok || !p.data) throwFromRes(p);
  return p.data.balance?.netPaidCents ?? 0;
}

/**
 * Masanı azad et (status → 'free'). pos_local close masanı 'dirty' edir və
 * currentOrderId-i SAXLAYIR; 'free' isə currentOrderId-i təmizləyir → masa BOŞ
 * görünür (həm masa grid-də, həm kassada) və yeni sifariş üçün hazır olur.
 * Best-effort: azad etmə alınmasa ödəniş onsuz da uğurludur.
 */
async function freeTable(tableId: string | null | undefined): Promise<void> {
  if (!tableId) return;
  await posRequest({
    method: 'POST',
    path: `/v1/tables/${tableId}/status`,
    body: { toStatus: 'free' },
  });
}

// ── Müştəri balansı ──────────────────────────────────────────────────────────
//
// ═══ NİYƏ BULUDA GEDİRİK ═══
//
// Müştəri hesabı (balans/borc) YALNIZ buludda yaşayır — pos_local-da `customers`
// cədvəli ümumiyyətlə yoxdur. Terminal isə `customer_balance` payını sadəcə
// `wallet` metoduna çevirib yazırdı və `customer_id`-ni ATIRDI:
//
//   ofisiant müştəri hesabına yazır → sifariş "ödənilmiş" bağlanır
//   müştərinin balansı isə OLDUĞU KİMİ qalır
//
// Yəni restoran pulu itirirdi. İndi balans buludda azaldılır və yalnız bu
// alınandan sonra ödəniş yerli bazaya yazılır.

interface ChargedCustomer {
  customerId: string | number;
  amount: number;
}

function cloudHeaders() {
  return {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  };
}

/** Buludda balansı geri qaytar (kompensasiya). Best-effort. */
async function refundCustomer(c: ChargedCustomer): Promise<void> {
  try {
    await axios.post(
      `${base_url}/customers/${c.customerId}/add-balance`,
      { amount: c.amount, description: 'Ödəniş tamamlanmadı — geri qaytarıldı' },
      cloudHeaders(),
    );
  } catch {
    // Kompensasiya alınmadı: pul müştəridən çıxıb, sifariş isə bağlanmayıb.
    // Uydurma "uğur" göstərməkdənsə susmaq daha az zərərlidir — ödəniş onsuz da
    // xəta ilə bitəcək və kassir yenidən cəhd edəcək.
  }
}

/**
 * Bütün `customer_balance` paylarını buludda çıxarır.
 * Biri alınmasa əvvəlkiləri geri qaytarıb xəta atır — yarımçıq vəziyyət qalmır.
 */
async function chargeCustomerBalances(shares: CloseBillShare[]): Promise<ChargedCustomer[]> {
  const charged: ChargedCustomer[] = [];

  for (const s of shares) {
    if ((s.type ?? '').toLowerCase() !== 'customer_balance') continue;

    const amount = Number(s.amount ?? 0);
    if (!Number.isFinite(amount) || amount <= 0) continue;

    // Müştəri seçilməyibsə balansdan ödəmək mümkün deyil — səssizcə "nağd"
    // kimi yazmaq pulun izini itirərdi.
    if (!s.customer_id) {
      throw posError(422, 'Balansdan ödəmək üçün müştəri seçilməlidir.', 'CUSTOMER_REQUIRED');
    }

    try {
      await axios.post(
        `${base_url}/customers/${s.customer_id}/withdraw`,
        { amount, description: 'POS sifariş ödənişi' },
        cloudHeaders(),
      );
      charged.push({ customerId: s.customer_id, amount });
    } catch (err: any) {
      for (const done of charged) await refundCustomer(done);

      const status = err?.response?.status;
      if (status === 422) {
        throw posError(422, err?.response?.data?.message || 'Müştəri balansı kifayət deyil.', 'INSUFFICIENT_BALANCE');
      }
      // Şəbəkə yoxdursa balansı yoxlamaq MÜMKÜN DEYİL. Oflayn "ödənildi"
      // yazmaq borcun itməsi deməkdir, ona görə açıq xəta veririk —
      // nağd/kart onsuz da işləyir.
      throw posError(
        503,
        'Müştəri balansı ilə ödəniş üçün internet lazımdır. Nağd və ya kartla ödəyin.',
        'CUSTOMER_BALANCE_OFFLINE',
      );
    }
  }

  return charged;
}

// ── HesapKes: POST /order/:id/payments → hesabı bağla ─────────────────────────
export async function runCloseBill(orderId: string, body: CloseBillBody): Promise<GatewayResult> {
  const shares = Array.isArray(body.shares) ? body.shares : [];

  // İdempotentlik: sifariş artıq bağlanıbsa (ikiqat klik / təkrar cəhd) yenidən
  // ödəniş yazıb close ETMƏ — 409 (ORDER_ALREADY_TERMINAL / balance 0) əvəzinə
  // "artıq ödənilib" uğuru qaytar. Order-dən tableId-ni də götürürük (azad etmək üçün).
  const existing = await posRequest<{ status?: string; tableId?: string | null }>({
    method: 'GET',
    path: `/v1/orders/${orderId}`,
  });
  const tableId = existing.ok ? existing.data?.tableId ?? null : null;
  const isTerminal = (s?: string) => s === 'closed' || s === 'void';

  if (existing.ok && isTerminal(existing.data?.status)) {
    // Artıq bağlanıb — masa hələ 'dirty' qalmış ola bilər → azad et.
    await freeTable(tableId);
    return { status: 200, data: { message: 'Sifariş artıq bağlanıb' } };
  }

  // Balans buludda ƏVVƏLCƏ çıxarılır: yerli ödəniş yazılıb order bağlandıqdan
  // sonra çıxarsaydıq və bulud rədd etsəydi, sifariş "ödənilmiş" qalardı.
  const charged = await chargeCustomerBalances(shares);

  try {
    // 0) Ödənişlər açıq shift-ə bağlansın (yoxdursa avtomatik aç) → kassada görünsün.
    await ensureOpenShift();

    // 1) Müştərinin verdiyi hər tender-i ödəniş kimi qeyd et.
    for (const s of shares) {
      await recordPayment(orderId, mapMethod(s.type), toCents(s.amount));
    }

    // 2) Endirim/ön-ödəniş səbəbindən balans total-dan azdırsa, fərqi `other` ilə bağla.
    const total = await fetchTotalCents(orderId);
    if (total > 0) {
      const netPaid = await fetchNetPaidCents(orderId);
      const gap = total - netPaid;
      if (gap > 0) await recordPayment(orderId, 'other', gap);
    }

    // 3) Order-i bağla.
    const close = await posRequest({
      method: 'POST',
      path: `/v1/orders/${orderId}/close`,
      body: {},
    });
    if (!close.ok) throwFromRes(close);
  } catch (e) {
    // Konkurent ikiqat-submit (TOCTOU): başqa çağırış order-i artıq bağlamış ola bilər
    // (ORDER_ALREADY_TERMINAL / balans 0). Order-i təzədən yoxla — terminaldırsa
    // bunu XƏTA deyil, "artıq ödənilib" UĞURU kimi qəbul et (istifadəçiyə 409 atma).
    const recheck = await posRequest<{ status?: string }>({
      method: 'GET',
      path: `/v1/orders/${orderId}`,
    });
    if (recheck.ok && isTerminal(recheck.data?.status)) {
      await freeTable(tableId);
      return { status: 200, data: { message: 'Sifariş artıq bağlanıb' } };
    }

    // Sifariş bağlanmadı → müştəridən çıxarılan pulu geri qaytar.
    for (const c of charged) await refundCustomer(c);
    throw e;
  }

  // 4) Masanı azad et — close onu 'dirty' (currentOrderId qalır) edir, ona görə
  //    ödənişdən sonra masa hələ DOLU görünür. 'free' edib təmizləyirik.
  await freeTable(tableId);

  return { status: 200, data: { message: 'Ödəniş uğurla icra olundu' } };
}

// ── OncedenOde: GET /order/:id/prepayments → ön ödənişlərin siyahısı ───────────
export async function listPrepayments(orderId: string): Promise<GatewayResult> {
  const p = await posRequest<PosPaymentsList>({
    method: 'GET',
    path: `/v1/orders/${orderId}/payments`,
  });
  if (!p.ok || !p.data) throwFromRes(p);

  const rows = (p.data.payments ?? [])
    .filter((r) => r.status === 'captured' && r.method !== 'other')
    .map((r) => ({
      id: r.id,
      amount: r.amountCents / 100,
      type: unmapMethod(r.method),
      date: new Date(r.paidAt).toISOString().slice(0, 10),
    }));

  return { status: 200, data: rows };
}

// ── OncedenOde: POST /order/:id/prepayments → qismən ödəniş (close ETMƏ) ───────
export async function addPrepayment(orderId: string, body: AddPrepaymentBody): Promise<GatewayResult> {
  const amountCents = toCents(body.amount);
  if (amountCents <= 0) {
    throw posError(400, 'Məbləğ müsbət olmalıdır', 'INVALID_AMOUNT');
  }
  await ensureOpenShift();
  const res = await posRequest({
    method: 'POST',
    path: `/v1/orders/${orderId}/payments`,
    body: { method: mapMethod(body.type), amountCents },
  });
  if (!res.ok) {
    // pos_local OVERPAYMENT (409) → front-un gözlədiyi mesaj/status-a uyğunlaşdır.
    if (res.error?.code === 'OVERPAYMENT') {
      throw posError(400, 'Amount exceeds the remaining balance.', 'OVERPAYMENT');
    }
    throwFromRes(res);
  }
  return { status: 201, data: { message: 'ok' } };
}

// ── OncedenOde: DELETE /order/:id/prepayments/:prepareId → ödənişi void et ─────
export async function deletePrepayment(orderId: string, prepareId: string): Promise<GatewayResult> {
  const res = await posRequest({
    method: 'POST',
    path: `/v1/orders/${orderId}/payments/${prepareId}/void`,
    body: { reason: 'prepayment removed' },
  });
  if (!res.ok) throwFromRes(res);
  return { status: 200, data: { message: 'ok' } };
}
